<template>
  <div class="scene" :class="{ without: mode === 'without', playing }">
    <svg class="scene-svg" viewBox="0 0 800 300" preserveAspectRatio="xMidYMax slice" role="img" :aria-label="`The forest in ${currentYear}`">
      <defs>
        <linearGradient id="sc-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" class="sky-top"/>
          <stop offset="100%" class="sky-bottom"/>
        </linearGradient>
        <linearGradient id="sc-ground" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" class="ground-top"/>
          <stop offset="100%" class="ground-bottom"/>
        </linearGradient>
        <linearGradient id="sc-beam" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#7ee0d6" stop-opacity="0.55"/>
          <stop offset="100%" stop-color="#7ee0d6" stop-opacity="0"/>
        </linearGradient>
        <radialGradient id="sc-sun" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stop-color="#fff6d6"/>
          <stop offset="60%" stop-color="#ffd98a" stop-opacity="0.75"/>
          <stop offset="100%" stop-color="#ffd98a" stop-opacity="0"/>
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="800" height="300" fill="url(#sc-sky)"/>
      <circle class="sun" :cx="120 + sunShift" cy="70" r="46" fill="url(#sc-sun)"/>
      <path class="hill far" d="M0 176 C 90 132 170 150 250 140 S 420 112 520 136 S 700 120 800 150 L800 300 L0 300 Z"/>
      <path class="hill near" d="M0 190 C 120 168 220 178 330 170 S 560 160 640 176 S 760 168 800 178 L800 300 L0 300 Z"/>
      <rect x="0" y="182" width="800" height="118" fill="url(#sc-ground)"/>

      <!-- trees, back to front -->
      <g v-for="tree in drawnTrees" :key="tree.id" :transform="`translate(${tree.x},${tree.y}) scale(${tree.scale})`" class="tree">
        <g v-if="tree.stump">
          <rect x="-4.5" y="-7" width="9" height="7" rx="1.5" class="stump"/>
          <ellipse cx="0" cy="-7" rx="4.5" ry="1.7" class="stump-top"/>
        </g>
        <g v-if="!tree.gone" :style="{ opacity: tree.opacity }" :transform="`scale(${tree.grow})`">
          <g class="sway" :style="{ animationDelay: tree.delay + 's', animationDuration: tree.speed + 's' }">
            <template v-if="tree.genus === 'spruce'">
              <rect x="-2.5" y="-24" width="5" height="24" class="trunk"/>
              <polygon points="0,-100 -13,-72 13,-72" class="c1"/>
              <polygon points="0,-88 -19,-55 19,-55" class="c2"/>
              <polygon points="0,-70 -25,-34 25,-34" class="c1"/>
              <polygon points="0,-50 -30,-12 30,-12" class="c2"/>
            </template>
            <template v-else-if="tree.genus === 'birch'">
              <rect x="-2.3" y="-96" width="4.6" height="96" class="birch-trunk"/>
              <rect x="-2.3" y="-70" width="3" height="2" class="birch-mark"/>
              <rect x="-0.5" y="-48" width="2.8" height="2" class="birch-mark"/>
              <rect x="-2.3" y="-26" width="3.2" height="2" class="birch-mark"/>
              <ellipse cx="0" cy="-76" rx="19" ry="27" class="b1"/>
              <ellipse cx="8" cy="-66" rx="12" ry="16" class="b2"/>
              <ellipse cx="-9" cy="-84" rx="10" ry="13" class="b2"/>
            </template>
            <template v-else-if="tree.genus === 'oak' || tree.genus === 'other'">
              <path d="M-4 0 L-3 -46 L3 -46 L4 0 Z" class="trunk"/>
              <circle cx="0" cy="-66" r="27" class="o1"/>
              <circle cx="-19" cy="-55" r="17" class="o2"/>
              <circle cx="19" cy="-57" r="19" class="o2"/>
              <circle cx="4" cy="-82" r="16" class="o2"/>
            </template>
            <template v-else-if="tree.grow < 0.42">
              <!-- a young pine is still conical -->
              <rect x="-2" y="-22" width="4" height="22" class="pine-trunk"/>
              <polygon points="0,-100 -15,-62 15,-62" class="p2"/>
              <polygon points="0,-80 -21,-38 21,-38" class="p1"/>
              <polygon points="0,-58 -25,-16 25,-16" class="p2"/>
            </template>
            <template v-else>
              <!-- a grown Scots pine: tall bare trunk, irregular crown in the top third -->
              <path d="M-3 0 L-2.1 -94 L2.1 -94 L3 0 Z" class="pine-trunk"/>
              <path d="M0 -70 L-12 -62 M0 -78 L13 -72" class="pine-branch"/>
              <ellipse cx="-3" cy="-93" rx="16" ry="9" class="p1"/>
              <ellipse cx="-15" cy="-84" rx="14" ry="8.5" class="p2"/>
              <ellipse cx="12" cy="-86" rx="15" ry="8.5" class="p1"/>
              <ellipse cx="0" cy="-77" rx="19" ry="9" class="p1"/>
              <ellipse cx="15" cy="-71" rx="11" ry="7" class="p2"/>
              <ellipse cx="-14" cy="-65" rx="10" ry="6.5" class="p2"/>
              <ellipse cx="2" cy="-98" rx="9" ry="5" class="p3"/>
            </template>
          </g>
          <g v-if="tree.sparkle > 0" class="sparkles" :style="{ opacity: tree.sparkle }">
            <circle cx="-14" cy="-70" r="2.4"/><circle cx="12" cy="-88" r="2"/><circle cx="16" cy="-60" r="1.6"/><circle cx="-6" cy="-98" r="1.8"/>
          </g>
        </g>
      </g>

      <!-- forestry machines: a small one with Zerticarbon, a heavy harvester without -->
      <g v-for="m in machines" :key="m.key" :transform="`translate(${m.x},240) scale(${m.scale})`" class="mach" :class="m.kind">
        <g class="chassis">
          <template v-if="m.kind === 'small'">
            <rect x="-30" y="-21" width="54" height="12" rx="3" class="body"/>
            <rect x="0" y="-38" width="19" height="19" rx="3" class="cab"/>
            <rect x="3" y="-35" width="13" height="9" rx="1.5" class="glass"/>
            <path d="M14 -31 L34 -47 L50 -29" class="boom"/>
            <rect x="46" y="-31" width="9" height="11" rx="2" class="head"/>
            <text x="-24" y="-12" class="logo">Z</text>
          </template>
          <template v-else>
            <rect x="-46" y="-34" width="92" height="20" rx="4" class="body"/>
            <rect x="-40" y="-50" width="7" height="17" class="pipe"/>
            <rect x="8" y="-60" width="28" height="28" rx="4" class="cab"/>
            <rect x="12" y="-56" width="20" height="13" rx="2" class="glass"/>
            <path d="M30 -50 L66 -76 L92 -42" class="boom"/>
            <rect x="86" y="-46" width="15" height="17" rx="3" class="head"/>
            <circle class="smoke s1" cx="-36" cy="-56" r="5"/><circle class="smoke s2" cx="-36" cy="-56" r="6"/><circle class="smoke s3" cx="-36" cy="-56" r="4"/>
          </template>
        </g>
        <template v-if="m.kind === 'small'">
          <circle cx="-20" cy="-7" r="7" class="wheel"/><circle cx="-4" cy="-7" r="7" class="wheel"/><circle cx="15" cy="-7" r="7" class="wheel"/>
        </template>
        <template v-else>
          <circle cx="-32" cy="-11" r="11" class="wheel"/><circle cx="-9" cy="-11" r="11" class="wheel"/><circle cx="17" cy="-11" r="11" class="wheel"/><circle cx="39" cy="-11" r="11" class="wheel"/>
        </template>
      </g>

      <!-- Zerticarbon's drone scans every tree with LiDAR -->
      <g v-if="mode === 'with'" class="drone-path">
        <g class="drone">
          <polygon points="-24,8 24,8 80,150 -80,150" fill="url(#sc-beam)" class="beam"/>
          <line x1="-74" y1="140" x2="74" y2="140" class="scanline"/>
          <line x1="-27" y1="-2" x2="27" y2="-2" class="arm"/>
          <ellipse cx="-28" cy="-5" rx="10" ry="2.2" class="rotor"/>
          <ellipse cx="28" cy="-5" rx="10" ry="2.2" class="rotor"/>
          <rect x="-12" y="-4" width="24" height="9" rx="4" class="hull"/>
          <circle cx="0" cy="6" r="2.6" class="lens"/>
        </g>
      </g>

      <!-- CO2 drawn into the forest -->
      <g class="bubbles" v-if="mode === 'with'">
        <g v-for="b in bubbles" :key="b.id" :transform="`translate(${b.x},${b.y})`">
          <g class="bubble" :class="{ add: b.add }" :style="{ animationDelay: b.delay + 's', animationDuration: b.speed + 's' }">
            <circle r="9"/>
            <text y="3" text-anchor="middle">{{ b.add ? '+' : 'CO₂' }}</text>
          </g>
        </g>
      </g>
    </svg>

    <div class="scene-top">
      <div class="scene-year"><b>{{ currentYear }}</b><span>forest age {{ Math.round(age + t) }} years</span></div>
      <div class="scene-figs">
        <div class="fig co2"><span>{{ mode === 'with' ? 'CO₂ certified' : 'CO₂ captured, not certified' }}</span><b>{{ fmtT(capturedAt) }} t</b></div>
        <template v-if="showMoney">
          <div class="fig money" v-if="mode === 'with'"><span>Earned</span><b>{{ eur(earnedAt) }}</b></div>
          <div class="fig money none" v-else><span>Earned</span><b>{{ eur(0) }}</b></div>
        </template>
      </div>
    </div>

    <transition-group name="evt" tag="div" class="scene-events">
      <div v-for="e in activeEvents" :key="e.key" class="scene-event" :class="e.kind"><i :class="e.icon"></i> {{ e.text }}</div>
    </transition-group>

    <div class="scene-bar" :class="{ 'with-note': mode === 'without' }">
      <div class="bar-note" v-if="mode === 'without'"><i class="fas fa-info-circle"></i> Without Zerticarbon: conventional management, nobody measures, certifies or sells its CO₂{{ cutAt && t >= cutAt ? `, and every tree was felled in ${startYear + cutAt}` : '' }}.</div>
      <button type="button" class="play" @click="toggle" :aria-label="playing ? 'Pause' : 'Play'">
        <i class="fas" :class="playing ? 'fa-pause' : (t >= period ? 'fa-redo' : 'fa-play')"></i>
      </button>
      <input type="range" min="0" :max="period" step="0.05" :value="t" @input="seek($event.target.value)" aria-label="Year">
      <span class="range-label">{{ startYear }}–{{ startYear + period }}</span>
      <div class="mode">
        <button type="button" :class="{ on: mode === 'with' }" @click="mode = 'with'">With Zerticarbon</button>
        <button type="button" :class="{ on: mode === 'without' }" @click="mode = 'without'">Without</button>
      </div>
    </div>
  </div>
</template>

<script>
// One fixed layout of trees per page: positions are pseudo-random but stable (seeded), so the scene never jumps.
function seeded(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Playback speed in years per second: the years go by quickly, but whatever happens in the forest is played slowly
// enough to be seen, whatever the length of the project
const NORMAL_RATE = 1 / 0.3; // 30 years in 9 seconds
const MACHINE_YEARS = 1.2; // a machine crosses the forest in this much of the timeline...
const MACHINE_SECONDS = 5; // ...which takes this long on screen

export default {
  name: 'SimForestScene',
  props: {
    species: { type: Array, default: () => [] }, // [{ genus, share }]
    density: { type: Number, default: 900 },
    age: { type: Number, default: 30 },
    period: { type: Number, default: 30 },
    startYear: { type: Number, default: new Date().getFullYear() },
    years: { type: Array, default: () => [] }, // simulate().years
    events: { type: Object, default: () => ({}) }, // { fertilizing: [t], fertilizer, thinning: { at, pct, convPct, target }, finalCut: at }
    autoplay: { type: Boolean, default: false },
    showMoney: { type: Boolean, default: true },
  },
  data() {
    return { t: 0, playing: false, mode: 'with', frame: null, last: 0 };
  },
  computed: {
    currentYear() { return this.startYear + Math.round(this.t); },
    cutAt() { return this.events.finalCut || null; },
    layout() {
      const rand = seeded(7919);
      const count = Math.max(14, Math.min(46, Math.round(this.density / 28)));
      const trees = [];
      for (let i = 0; i < count; i += 1) {
        const depth = rand();
        trees.push({
          id: i,
          x: 20 + rand() * 760,
          depth,
          y: 186 + depth * 102,
          base: 0.42 + depth * 0.78,
          pick: rand(),
          thin: rand(),
          delay: -rand() * 4,
          speed: 3.4 + rand() * 2.4,
          variety: 0.85 + rand() * 0.3,
        });
      }
      return trees.sort((a, b) => a.y - b.y);
    },
    mix() {
      const rows = this.species.filter((s) => s.share > 0);
      const total = rows.reduce((a, s) => a + s.share, 0) || 1;
      let acc = 0;
      return rows.map((s) => { acc += s.share / total; return { genus: s.genus, upTo: acc }; });
    },
    drawnTrees() {
      const t = this.t;
      const thin = this.events.thinning;
      const fert = this.events.fertilizing || [];
      const managed = this.mode === 'with';
      const growth = (a) => Math.max(0.07, 1 - Math.exp(-Math.max(0, a) / 30));
      // how far the machine that starts at `at` has cut the tree at x: 0 not yet, 1 felled
      const cutBy = (at, x) => {
        if (t < at) return 0;
        if (t >= at + MACHINE_YEARS) return 1;
        const d = this.machineX(at) - x;
        return d > 14 ? 1 : d < -14 ? 0 : (d + 14) / 28;
      };
      return this.layout.map((tree) => {
        const genus = (this.mix.find((m) => tree.pick <= m.upTo) || this.mix[this.mix.length - 1] || { genus: 'pine' }).genus;
        const grow = growth(this.age + t) * tree.variety;
        let felled = 0;
        // both thin; the conventional harvester takes more trees (a superset of ours)
        if (thin && tree.thin < (managed ? thin.pct : thin.convPct)) felled = cutBy(thin.at, tree.x);
        // without Zerticarbon the final cut fells every tree
        if (!managed && this.cutAt) felled = Math.max(felled, cutBy(this.cutAt, tree.x));
        let sparkle = 0;
        if (managed) for (const a of fert) if (t >= a - 0.2 && t <= a + 1.4) sparkle = Math.max(sparkle, 1 - Math.abs(t - a - 0.4) / 1);
        return { ...tree, genus, grow, stump: felled > 0, opacity: 1 - felled, gone: felled >= 1, sparkle: Math.max(0, sparkle), scale: tree.base };
      });
    },
    machines() {
      const list = [];
      const t = this.t;
      const th = this.events.thinning;
      const managed = this.mode === 'with';
      const running = (at) => t >= at && t < at + MACHINE_YEARS;
      if (th && running(th.at)) list.push({ key: 'thin', kind: managed ? 'small' : 'big', x: this.machineX(th.at), scale: managed ? 1 : 1.3 });
      if (!managed && this.cutAt && running(this.cutAt)) list.push({ key: 'cut', kind: 'big', x: this.machineX(this.cutAt), scale: 1.4 });
      return list;
    },
    bubbles() {
      const rand = seeded(104729);
      const additional = (this.events.fertilizing && this.events.fertilizing.length) || this.events.thinning || this.cutAt;
      return Array.from({ length: 12 }, (_, i) => ({ id: i, x: 60 + rand() * 680, y: 150 + rand() * 40, delay: -rand() * 6, speed: 5 + rand() * 4, add: additional && i % 3 === 0 }));
    },
    capturedAt() { return this.mode === 'with' ? this.valueAt((y) => y.cumBaseTons + y.cumAddTons) : this.valueAt((y) => y.cumConvTons); },
    earnedAt() { return this.valueAt((y) => y.cumNet); },
    sunShift() { return (this.t / Math.max(1, this.period)) * 520; },
    activeEvents() {
      const list = [];
      const t = this.t;
      const near = (at) => t >= at - 0.05 && t < at + 1.6;
      const th = this.events.thinning;
      const pct = (v) => Math.round((v || 0) * 100);
      if (this.mode === 'with') {
        if (t < 1.4) list.push({ key: 'drone', kind: 'drone', icon: 'fas fa-satellite-dish', text: 'Drone LiDAR scan: every tree measured' });
        for (const a of this.events.fertilizing || []) if (near(a)) list.push({ key: `f${a}`, kind: 'fert', icon: 'fas fa-seedling', text: `${this.startYear + a} · ${this.events.fertilizer || 'Fertilizing'}: the forest grows faster` });
        if (th && near(th.at)) list.push({ key: 'th', kind: 'thin', icon: 'fas fa-cut', text: `${this.startYear + th.at} · Small machines thin ${pct(th.pct)}% of the trees` });
        if (this.cutAt && near(this.cutAt)) list.push({ key: 'cut', kind: 'keep', icon: 'fas fa-shield-alt', text: `${this.startYear + this.cutAt} · Final cut avoided: the forest keeps capturing` });
      } else {
        if (th && near(th.at)) list.push({ key: 'thw', kind: 'thin', icon: 'fas fa-cut', text: `${this.startYear + th.at} · Conventional harvester: ${pct(th.convPct)}% of the trees cut` });
        if (this.cutAt && near(this.cutAt)) list.push({ key: 'cutw', kind: 'cut', icon: 'fas fa-tree', text: `${this.startYear + this.cutAt} · Clear-cut: every tree felled, no more CO₂ captured` });
      }
      return list;
    },
  },
  watch: {
    period() { if (this.t > this.period) this.t = this.period; },
  },
  mounted() {
    if (this.autoplay) this.play();
  },
  beforeDestroy() {
    if (this.frame) cancelAnimationFrame(this.frame);
  },
  methods: {
    fmtT(v) { return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(v || 0); },
    eur(v) { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0); },
    valueAt(get) {
      if (!this.years.length || this.t <= 0) return 0;
      const i = Math.min(this.years.length, this.t);
      const lo = Math.floor(i);
      const frac = i - lo;
      const a = lo >= 1 ? get(this.years[lo - 1]) : 0;
      const b = lo < this.years.length ? get(this.years[lo]) : a;
      return a + (b - a) * frac;
    },
    /** Where a machine that starts at `at` is now (it drives across the forest, left to right). */
    machineX(at) { return -110 + ((this.t - at) / MACHINE_YEARS) * 1020; },
    /** Years per second at this point of the timeline: slow while something happens, so the owner sees it. */
    rateAt(t) {
      const th = this.events.thinning;
      const managed = this.mode === 'with';
      const busy = (at, years) => t >= at - 0.1 && t < at + years;
      let rate = NORMAL_RATE;
      if ((th && busy(th.at, MACHINE_YEARS)) || (!managed && this.cutAt && busy(this.cutAt, MACHINE_YEARS))) rate = Math.min(rate, MACHINE_YEARS / MACHINE_SECONDS);
      if (managed && t < 1) rate = Math.min(rate, 1 / 3); // the drone's scan
      if (managed && (this.events.fertilizing || []).some((a) => busy(a, 1))) rate = Math.min(rate, 1 / 2.2);
      if (managed && this.cutAt && busy(this.cutAt, 1)) rate = Math.min(rate, 1 / 2.5); // the final cut that does not happen
      return rate;
    },
    seek(v) { this.pause(); this.t = Math.max(0, Math.min(this.period, Number(v))); },
    toggle() { if (this.playing) this.pause(); else this.play(); },
    play() {
      if (this.t >= this.period) this.t = 0;
      this.playing = true;
      this.last = performance.now();
      const step = (now) => {
        const dt = (now - this.last) / 1000;
        this.last = now;
        this.t = Math.min(this.period, this.t + dt * this.rateAt(this.t));
        if (this.t >= this.period) { this.playing = false; this.frame = null; return; }
        this.frame = requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    },
    pause() {
      this.playing = false;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    },
  },
};
</script>

<style lang="scss">
/* dark theme of the scene (the theme attribute lives on <html>, outside the scoped styles) */
[data-theme="dark"] .scene {
  .sky-top { stop-color: #0d2230; }
  .sky-bottom { stop-color: #1d3b2c; }
  .ground-top { stop-color: #2c5a33; }
  .ground-bottom { stop-color: #15301b; }
  .hill.far { fill: #1f4430; }
  .hill.near { fill: #275436; }
  .sun { opacity: 0.55; }
}
</style>

<style scoped lang="scss">
.scene {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  min-height: 280px;
  background: #cfe8d2;
  isolation: isolate;
}

.scene-svg {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 280px;
}

.sky-top { stop-color: #bfe3f2; }
.sky-bottom { stop-color: #eef8ef; }
.ground-top { stop-color: #8cc07a; }
.ground-bottom { stop-color: #4f8c4a; }
.hill.far { fill: #9fcf9a; opacity: 0.75; }
.hill.near { fill: #7db56f; }

.without {
  .hill.near, .ground-top { filter: saturate(0.75); }
}

.stump { fill: #8b5a3a; }
.stump-top { fill: #d7b48a; }

/* machines */
.mach .wheel { fill: #262626; stroke: #555; stroke-width: 2; }
.mach .glass { fill: #c9ecf5; }
.mach .chassis { animation: bob 0.32s ease-in-out infinite alternate; }
@keyframes bob { from { transform: translateY(0); } to { transform: translateY(-1.2px); } }
.mach.small {
  .body { fill: #4f8c4a; }
  .cab { fill: #3d7a3f; }
  .boom { fill: none; stroke: #2f5a33; stroke-width: 3; stroke-linejoin: round; }
  .head { fill: #f4c25b; }
  .logo { font-size: 8px; font-weight: 800; fill: #fff; font-family: 'Sora', sans-serif; }
}
.mach.big {
  .body { fill: #e0a52e; }
  .cab { fill: #c98a1a; }
  .pipe { fill: #444; }
  .boom { fill: none; stroke: #6b4a10; stroke-width: 5; stroke-linejoin: round; }
  .head { fill: #4a4a4a; }
  .smoke { fill: rgba(90, 90, 90, 0.35); animation: smoke 1.4s ease-out infinite; }
  .s2 { animation-delay: 0.45s; }
  .s3 { animation-delay: 0.9s; }
}
@keyframes smoke {
  from { transform: translate(0, 0) scale(0.6); opacity: 0.8; }
  to { transform: translate(-14px, -26px) scale(1.8); opacity: 0; }
}

/* drone */
.drone-path { animation: drone-fly 8s linear infinite; }
@keyframes drone-fly {
  from { transform: translate(-120px, 34px); }
  to { transform: translate(920px, 34px); }
}
.drone { animation: drone-bob 1.6s ease-in-out infinite alternate; }
@keyframes drone-bob { from { transform: translateY(0); } to { transform: translateY(5px); } }
.drone .hull { fill: #f2f6f3; stroke: #1f3a25; stroke-width: 1.2; }
.drone .arm { stroke: #1f3a25; stroke-width: 2.4; }
.drone .rotor { fill: rgba(255, 255, 255, 0.55); stroke: rgba(31, 58, 37, 0.5); stroke-width: 0.8; animation: rotor 0.12s linear infinite alternate; }
@keyframes rotor { from { transform: scaleX(1); } to { transform: scaleX(0.35); } }
.drone .rotor { transform-box: fill-box; transform-origin: center; }
.drone .lens { fill: #2fd0c4; }
.drone .beam { opacity: 0.9; }
.drone .scanline { stroke: #c9f7b3; stroke-width: 1.6; stroke-dasharray: 3 3; animation: scan 1.8s ease-in-out infinite alternate; }
@keyframes scan { from { transform: translateY(0); opacity: 0.9; } to { transform: translateY(-110px); opacity: 0.3; } }

.tree { transition: opacity 0.4s; }
.sway {
  animation: sway 4s ease-in-out infinite alternate;
}
@keyframes sway {
  from { transform: rotate(-1.4deg); }
  to { transform: rotate(1.4deg); }
}

.trunk { fill: #7b5236; }
.pine-trunk { fill: #a0603a; }
.birch-trunk { fill: #f1efe8; }
.birch-mark { fill: #2b2b2b; }
.c1 { fill: #1f5a3a; }
.c2 { fill: #2b6e48; }
.p1 { fill: #2f6b3f; }
.p2 { fill: #437f45; }
.p3 { fill: #5d9a52; }
.pine-branch { stroke: #8a5232; stroke-width: 1.6; fill: none; }
.b1 { fill: #8fc36b; }
.b2 { fill: #a9d68a; }
.o1 { fill: #3d7a3f; }
.o2 { fill: #579a4c; }

.sparkles circle {
  fill: #fff6b0;
  filter: drop-shadow(0 0 3px #fff2a0);
}

.bubble {
  animation: rise 7s linear infinite;
  opacity: 0;

  circle { fill: rgba(47, 208, 196, 0.28); stroke: rgba(255, 255, 255, 0.75); stroke-width: 1; }
  text { font-size: 6px; font-weight: 800; fill: #fff; font-family: 'Sora', sans-serif; }
  &.add circle { fill: rgba(158, 215, 127, 0.45); }
  &.add text { font-size: 11px; }
}
@keyframes rise {
  0% { transform: translateY(40px) scale(0.6); opacity: 0; }
  15% { opacity: 0.95; }
  100% { transform: translateY(-150px) scale(1.1); opacity: 0; }
}

.scene-top {
  position: absolute;
  top: 12px;
  left: 14px;
  right: 14px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  pointer-events: none;
}

.scene-year {
  display: flex;
  flex-direction: column;
  padding: 8px 14px;
  border-radius: 14px;
  background: rgba(12, 24, 16, 0.55);
  backdrop-filter: blur(6px);
  color: #fff;

  b { font-family: 'Sora', sans-serif; font-size: 1.5rem; line-height: 1; font-variant-numeric: tabular-nums; }
  span { font-size: 0.72rem; opacity: 0.85; margin-top: 2px; }
}

.scene-figs { display: flex; gap: 8px; }

.fig {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 8px 14px;
  border-radius: 14px;
  background: rgba(12, 24, 16, 0.55);
  backdrop-filter: blur(6px);
  color: #fff;

  span { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.8; }
  b { font-family: 'Sora', sans-serif; font-size: 1.15rem; font-variant-numeric: tabular-nums; }
  &.money b { color: #c9f7b3; }
  &.co2 b { color: #7ee0d6; }
  &.none b { color: #ffb3a0; }
}

.scene-events {
  position: absolute;
  top: 74px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  pointer-events: none;
  width: max-content;
  max-width: 92%;
}

.scene-event {
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
  background: rgba(79, 140, 74, 0.92);
  box-shadow: 0 8px 22px -8px rgba(0, 0, 0, 0.5);
  &.thin { background: rgba(224, 165, 46, 0.95); }
  &.keep { background: rgba(21, 152, 149, 0.95); }
  &.cut { background: rgba(224, 106, 68, 0.95); }
  &.drone { background: rgba(21, 152, 149, 0.95); }
}

.evt-enter-active, .evt-leave-active { transition: all 0.35s cubic-bezier(.2, .8, .2, 1); }
.evt-enter, .evt-leave-to { opacity: 0; transform: translateY(-8px) scale(0.96); }

.scene-bar {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  padding: 6px 8px;
  border-radius: 14px;
  background: rgba(12, 24, 16, 0.6);
  backdrop-filter: blur(6px);
  color: #fff;

  input[type=range] { flex: 1; min-width: 60px; accent-color: #9ed77f; }
}

.bar-note {
  flex: 0 0 100%;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.3;
  color: #ffd0c2;
  padding: 0 4px;
  i { margin-right: 4px; }
}

.play {
  width: 36px;
  height: 36px;
  flex: none;
  border: 0;
  border-radius: 50%;
  color: #12301a;
  background: linear-gradient(135deg, #c9f7b3, #6bb15e);
  box-shadow: 0 0 0 0 rgba(158, 215, 127, 0.6);
  animation: play-ping 2.2s infinite;
}
.playing .play { animation: none; }
@keyframes play-ping {
  0% { box-shadow: 0 0 0 0 rgba(158, 215, 127, 0.6); }
  100% { box-shadow: 0 0 0 12px rgba(158, 215, 127, 0); }
}

.range-label { font-size: 0.72rem; opacity: 0.8; white-space: nowrap; }

.mode {
  display: flex;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.25);

  button {
    border: 0;
    padding: 5px 10px;
    font-size: 0.72rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.8);
    background: transparent;
    white-space: nowrap;
    &.on { background: #fff; color: #1f3a25; }
  }
}

@media (max-width: 575px) {
  .scene-figs .fig { padding: 6px 10px; }
  .fig b { font-size: 0.95rem; }
  .scene-year b { font-size: 1.2rem; }
  .range-label { display: none; }
  .scene-events { top: 118px; }
}
</style>
